// wsClient.js — singleton WebSocket with keepalive + reconnect hooks
let ws = null;
let listeners = new Set();
let reconnectListeners = new Set();
let reconnectTimer = null;
let pingTimer = null;
let lastConfig = null;
let intentionalClose = false;

const PING_MS = 20000;
const RECONNECT_MS = 1500;

function clearTimers() {
  clearTimeout(reconnectTimer);
  clearInterval(pingTimer);
  reconnectTimer = null;
  pingTimer = null;
}

function startPing() {
  clearInterval(pingTimer);
  pingTimer = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: 'ping', t: Date.now() }));
      } catch {
        /* ignore */
      }
    }
  }, PING_MS);
}

function notifyReconnect() {
  reconnectListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

export function ensureWS(url, token) {
  lastConfig = { url, token };
  intentionalClose = false;

  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return ws;
  }

  clearTimers();
  ws = new WebSocket(url, token);

  ws.onopen = () => {
    console.log('[ws] singleton connected');
    startPing();
    notifyReconnect();
  };

  ws.onmessage = (ev) => {
    listeners.forEach((fn) => fn(ev));
  };

  ws.onerror = (e) => console.log('[ws] singleton error', e);

  ws.onclose = () => {
    console.log('[ws] singleton closed, retrying...');
    clearInterval(pingTimer);
    pingTimer = null;
    if (intentionalClose) return;
    clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => {
      ws = null;
      if (lastConfig) ensureWS(lastConfig.url, lastConfig.token);
    }, RECONNECT_MS);
  };

  return ws;
}

/** Force close + reconnect (e.g. after tab wakes on iPad). */
export function reconnectWS() {
  if (!lastConfig) return;
  intentionalClose = false;
  try {
    if (ws) {
      intentionalClose = true;
      ws.close();
    }
  } catch {
    /* ignore */
  }
  ws = null;
  intentionalClose = false;
  ensureWS(lastConfig.url, lastConfig.token);
}

export function isWSOpen() {
  return !!(ws && ws.readyState === WebSocket.OPEN);
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Called after every successful WS open (including reconnects). */
export function onReconnect(fn) {
  reconnectListeners.add(fn);
  return () => reconnectListeners.delete(fn);
}
